import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Users, Loader2, ExternalLink, Gift, Store, Globe, Ban, CheckCircle2, Clock,
} from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { money } from "@/lib/format";
import DashLayout from "@/components/xuma/DashLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const STATUS_META = {
  trialing: { label: "En prueba", cls: "bg-blue-50 text-blue-700", icon: Clock },
  active: { label: "Activo", cls: "bg-green-50 text-green-700", icon: CheckCircle2 },
  free: { label: "Cortesía", cls: "bg-amber-50 text-amber-700", icon: Gift },
  cancelled: { label: "Cancelado", cls: "bg-red-50 text-red-700", icon: Ban },
  none: { label: "Sin suscripción", cls: "bg-stone-100 text-stone-600", icon: Ban },
};

const fmtDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("es-MX", { day: "2-digit", month: "short", year: "numeric" }); }
  catch { return "—"; }
};

export default function AdminClients() {
  const { user, loading } = useAuth();
  const [clients, setClients] = useState([]);
  const [loadingData, setLoadingData] = useState(true);
  const [grantFor, setGrantFor] = useState(null);

  const load = async () => {
    const { data } = await api.get("/admin/clients");
    setClients(data);
    setLoadingData(false);
  };
  useEffect(() => { if (user?.role === "admin") load().catch(() => setLoadingData(false)); }, [user]);

  if (loading) return <div className="min-h-screen grid place-items-center"><Loader2 className="h-8 w-8 animate-spin text-xuma-primary" /></div>;
  if (user?.role !== "admin") return <Navigate to="/dashboard" replace />;

  const freeCount = clients.filter((c) => c.free_active).length;
  const monthlyMRR = clients
    .filter((c) => !c.free_active && (c.subscription_status === "active" || c.subscription_status === "trialing"))
    .reduce((a, c) => a + (c.monthly_fee || 0), 0);

  const revoke = async (c) => {
    try {
      await api.post(`/admin/clients/${c.user_id}/revoke-free`);
      toast.success("Cortesía retirada. Se reanuda el cobro.");
      await load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  return (
    <DashLayout title="Clientes">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <Kpi icon={Users} label="Clientes" value={clients.length} />
        <Kpi icon={Gift} label="En cortesía" value={freeCount} />
        <Kpi icon={Store} label="Por distribuidor" value={clients.filter((c) => c.source === "distributor").length} />
        <Kpi icon={Globe} label="MRR estimado" value={money(monthlyMRR)} />
      </div>

      {loadingData ? (
        <div className="grid place-items-center py-16"><Loader2 className="h-6 w-6 animate-spin text-xuma-primary" /></div>
      ) : (
        <div className="rounded-2xl bg-white border border-xuma-border overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[860px] text-sm">
              <thead>
                <tr className="border-b border-xuma-border text-left text-xs uppercase tracking-wider text-xuma-muted">
                  {["Cliente", "Plan", "Origen", "Estado", "Tarjeta", ""].map((h, i) => (
                    <th key={i} className={`px-4 py-3 ${i === 5 ? "text-right" : ""}`}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {clients.map((c) => {
                  const meta = STATUS_META[c.free_active ? "free" : (c.subscription_status || "none")] || STATUS_META.none;
                  const StatusIcon = meta.icon;
                  const hasPlan = c.plan_key && c.plan_key !== "none";
                  return (
                    <tr key={c.user_id} data-testid={`client-row-${c.user_id}`} className="border-b border-xuma-border last:border-0 hover:bg-xuma-dash/40 transition-colors">
                      <td className="px-4 py-3">
                        <p className="font-medium text-xuma-dark">{c.name}</p>
                        <p className="text-xs text-xuma-muted">{c.email}</p>
                      </td>
                      <td className="px-4 py-3">
                        {hasPlan ? (
                          <>
                            <p className="text-xuma-dark">{c.plan_label}</p>
                            {c.monthly_fee != null && <p className="text-xs text-xuma-muted">{money(c.monthly_fee)} / mes</p>}
                          </>
                        ) : <span className="text-xuma-muted">Sin plan</span>}
                      </td>
                      <td className="px-4 py-3">
                        {c.source === "distributor"
                          ? <span className="inline-flex items-center gap-1 text-xuma-muted"><Store className="h-3.5 w-3.5" /> {c.distributor_name || "Distribuidor"}</span>
                          : <span className="inline-flex items-center gap-1 text-xuma-muted"><Globe className="h-3.5 w-3.5" /> Directo</span>}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center gap-1 text-xs rounded-full px-2.5 py-1 font-medium ${meta.cls}`}>
                          <StatusIcon className="h-3.5 w-3.5" /> {meta.label}
                        </span>
                        {c.free_active && <p className="mt-1 text-[11px] text-xuma-muted">hasta {fmtDate(c.free_until)}</p>}
                      </td>
                      <td className="px-4 py-3">
                        {c.card_slug
                          ? <a href={`/c/${c.card_slug}`} target="_blank" rel="noreferrer" className="text-xuma-primary inline-flex items-center gap-1 hover:underline">Ver <ExternalLink className="h-3.5 w-3.5" /></a>
                          : <span className="text-xuma-muted">—</span>}
                      </td>
                      <td className="px-4 py-3 text-right whitespace-nowrap">
                        {c.free_active ? (
                          <div className="inline-flex gap-2">
                            <Button size="sm" variant="outline" data-testid={`grant-free-${c.user_id}`} onClick={() => setGrantFor(c)} className="rounded-full h-8 px-3 gap-1 border-xuma-border">
                              <Gift className="h-3.5 w-3.5" /> Extender
                            </Button>
                            <Button size="sm" variant="ghost" data-testid={`revoke-free-${c.user_id}`} onClick={() => revoke(c)} className="rounded-full h-8 px-3 gap-1 text-red-600 hover:text-red-700 hover:bg-red-50">
                              <Ban className="h-3.5 w-3.5" /> Retirar
                            </Button>
                          </div>
                        ) : (
                          <Button size="sm" data-testid={`grant-free-${c.user_id}`} onClick={() => setGrantFor(c)} className="rounded-full h-8 px-4 gap-1 bg-xuma-primary hover:bg-xuma-primaryHover text-white">
                            <Gift className="h-3.5 w-3.5" /> Dar gratis
                          </Button>
                        )}
                      </td>
                    </tr>
                  );
                })}
                {clients.length === 0 && (
                  <tr><td colSpan={6} className="px-4 py-12 text-center text-xuma-muted">Aún no hay clientes registrados.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <GrantFreeDialog client={grantFor} onClose={() => setGrantFor(null)} onDone={() => { setGrantFor(null); load(); }} />
    </DashLayout>
  );
}

function GrantFreeDialog({ client, onClose, onDone }) {
  const [preset, setPreset] = useState("30");
  const [custom, setCustom] = useState("");
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (client) { setPreset("30"); setCustom(""); } }, [client]);
  if (!client) return null;

  const days = preset === "custom" ? parseInt(custom || "0", 10) : parseInt(preset, 10);
  const submit = async () => {
    if (!days || days < 1) { toast.error("Indica un número de días válido."); return; }
    setSaving(true);
    try {
      const { data } = await api.post(`/admin/clients/${client.user_id}/grant-free`, { days });
      const until = new Date(data.free_until).toLocaleDateString("es-MX", { day: "2-digit", month: "long", year: "numeric" });
      toast.success(`Cortesía activa hasta el ${until}${data.stripe_applied ? "" : " (sin suscripción Stripe, aplicado en el sistema)"}`);
      onDone();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={!!client} onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="font-heading">Dar acceso gratis</DialogTitle>
          <DialogDescription>
            {client.free_active
              ? <>Extiende la cortesía de <b>{client.name}</b>. Los días se suman a la fecha actual.</>
              : <>Otorga acceso sin cobro a <b>{client.name}</b> por el tiempo que decidas. La mensualidad de Stripe se pospone automáticamente.</>}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label className="mb-1 block">Duración</Label>
            <Select value={preset} onValueChange={setPreset}>
              <SelectTrigger data-testid="free-preset"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="30">1 mes (30 días)</SelectItem>
                <SelectItem value="90">3 meses (90 días)</SelectItem>
                <SelectItem value="180">6 meses (180 días)</SelectItem>
                <SelectItem value="365">1 año (365 días)</SelectItem>
                <SelectItem value="custom">Personalizado…</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {preset === "custom" && (
            <div>
              <Label className="mb-1 block">Número de días</Label>
              <Input data-testid="free-custom-days" type="number" min="1" value={custom} onChange={(e) => setCustom(e.target.value)} placeholder="Ej. 45" />
            </div>
          )}
          {!client.has_subscription && (
            <p className="text-xs text-amber-700 bg-amber-50 rounded-lg px-3 py-2">
              Este cliente no tiene suscripción de Stripe vinculada; la cortesía se registra solo en el sistema.
            </p>
          )}
        </div>
        <DialogFooter>
          <Button data-testid="free-submit" onClick={submit} disabled={saving} className="bg-xuma-primary hover:bg-xuma-primaryHover text-white">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Confirmar cortesía"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const Kpi = ({ icon: Icon, label, value }) => (
  <div className="rounded-2xl bg-white border border-xuma-border p-5">
    <Icon className="h-5 w-5 text-xuma-primary" />
    <p className="mt-3 font-heading font-extrabold text-2xl text-xuma-dark">{value}</p>
    <p className="text-sm text-xuma-muted">{label}</p>
  </div>
);
